/**
 * Compose the engine stages into a single apply plan. Pure, no Word dependency, unit-tested.
 *
 *   accepted baseline → tokenize → word diff → char explosion → interleave existing deletions →
 *     locate ops
 *
 * The diff runs against the **accepted** state (existing deletions removed) vs the AI target — both
 * clean — so it never corrupts the target. Existing deletions are woven back in as `keep` at their
 * exact offsets by `interleaveExistingDeletions`, so they stay struck and untouched. This replaces
 * the old `buildVisibleTarget`, which re-injected deletions at char-diff-guessed positions and split
 * words mid-token on a rephrase.
 *
 * The Word-touching pre-flight search and execution consume this plan's `located` ops.
 */

import { tokenize } from './tokenize';
import { diffWords } from './diffWords';
import { explodePairs } from './explodePairs';
import {
  removeDeletionSpans,
  interleaveExistingDeletions,
  targetVisibleFromOps,
  deletionSpansOf,
} from './visibleOps';
import { locateOps, LocatedOp } from './locateOps';
import { ProtectedRegionSet } from './protectedRegions';
import { RevisionContext, OpHistogram, opHistogram, Op } from './types';
import { foldApplyText } from './applyVerify';

/** QR "ראה תיקון מוצע" (and siblings) must append, not re-diff live wording vs a folded CR. */
export function qrSuffixIfPureAppend(baselineVisible: string, cleanTarget: string): string | null {
  const baseline = foldApplyText(baselineVisible);
  const target = foldApplyText(cleanTarget);
  const suffixMatch = target.match(/ (\[[^\]]+\])$/);
  if (!suffixMatch) return null;
  const suffix = suffixMatch[0];
  if (target.slice(0, -suffix.length) !== baseline) return null;
  return suffix;
}

export interface ApplyPlan {
  /** The AI target re-expressed in the baseline's visible representation (T-006). */
  targetVisible: string;
  /** Final op sequence after word diff + char explosion, in baseline order. */
  ops: Op[];
  /** Actionable ops resolved to occurrence/anchor + protected flags (T-008). */
  located: LocatedOp[];
  /** Text-free op-kind counts for the trace. */
  histogram: OpHistogram;
  /** Count of located ops flagged as touching a protected region. */
  protectedOpCount: number;
}

/**
 * Build the full apply plan for a paragraph. Pure.
 *
 * @param baselineVisible  the live visible paragraph text (what Range.search sees)
 * @param cleanTarget      the AI/manual clean accepted-state target
 * @param protectedSet     field/bookmark guard (T-007)
 * @param revisionContext  pre-existing revisions in the paragraph (default: none)
 */
export function buildApplyPlan(
  baselineVisible: string,
  cleanTarget: string,
  protectedSet: ProtectedRegionSet,
  revisionContext: RevisionContext = { revisions: [] },
): ApplyPlan {
  const deletionSpans = deletionSpansOf(revisionContext.revisions);
  const qrSuffix = qrSuffixIfPureAppend(baselineVisible, cleanTarget);
  if (qrSuffix) {
    const ops: Op[] = [];
    if (baselineVisible) ops.push({ kind: 'keep', value: baselineVisible });
    ops.push({ kind: 'insert', value: qrSuffix });
    const located = locateOps(ops, baselineVisible, protectedSet);
    return {
      targetVisible: `${baselineVisible}${qrSuffix}`,
      ops,
      located,
      histogram: opHistogram(ops),
      protectedOpCount: located.filter((l) => l.protected).length,
    };
  }
  // Diff the ACCEPTED state vs the target (both clean) — never re-inject into the rephrased target.
  const acceptedBaseline = removeDeletionSpans(baselineVisible, deletionSpans);
  // Granular word diff (like the legacy preview) → char explosion for clean word edits. We do NOT
  // collapse into block replacements — the operator wants meaningful, granular redlines, not whole
  // sections struck when only part changed. The accepted-vs-target diff is already corruption-free.
  const acceptedOps = explodePairs(diffWords(tokenize(acceptedBaseline), tokenize(cleanTarget)));
  // Weave existing deletions back in as `keep` at their exact offsets → visible-space ops.
  const ops = interleaveExistingDeletions(acceptedOps, baselineVisible, deletionSpans);
  const located = locateOps(ops, baselineVisible, protectedSet);
  return {
    targetVisible: targetVisibleFromOps(ops),
    ops,
    located,
    histogram: opHistogram(ops),
    protectedOpCount: located.filter((l) => l.protected).length,
  };
}
